/**
 * useReservaciones — hook centralizado para cargar reservaciones y KPIs.
 * Cada sub-página filtra el resultado client-side por fecha/estado para
 * mantener la UI snappy y minimizar peticiones.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { API_BASE, getAuthHeaders, getEmpresaId, todayLocal } from './format';

async function fetchReservaciones(empresaId) {
  // Universo: reservas cuya salida sea de HOY en adelante — cubre las 5
  // pestañas del Booking (Hoy, Próximas, Hold, PorCobrar, Calendario). El
  // Histórico usa su propio endpoint dedicado (/historico), no depende de
  // este hook.
  //
  // Antes se pedía las últimas 500 por created_at DESC — con eso una
  // reserva creada con mucha anticipación (ej. venta hecha en julio para
  // salida hoy en agosto) se caía del subset porque entraron 500+ reservas
  // nuevas después, y desaparecía de Booking Hoy aunque su salida fuera hoy.
  // Filtrar por fecha de salida acota el subset por regla de negocio real,
  // no por ordenamiento de creación → ninguna reserva se pierde por antigua.
  const hoy = todayLocal();
  const url = `${API_BASE}/turismo/reservaciones`
    + `?empresa_id=${empresaId}`
    + `&salida_desde=${hoy}`
    + `&limit=5000`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Error cargando reservaciones');
  const data = await res.json();
  return Array.isArray(data) ? data : (data.reservaciones || []);
}

async function fetchKpis(empresaId) {
  const res = await fetch(`${API_BASE}/turismo/dashboard/resumen?empresa_id=${empresaId}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return {};
  return res.json();
}

export function useReservaciones() {
  const empresaId = getEmpresaId();
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ['booking', 'reservaciones', empresaId],
    queryFn: () => fetchReservaciones(empresaId),
    staleTime: 30 * 1000,
  });
  // refetch() se propaga por useOutletContext a todas las páginas (Hoy,
  // Próximas, Calendario, SlideOver, Wizard...) y lo llaman al confirmar
  // una mutación. Envolviéndolo aquí forzamos que UNA sola invocación
  // refresque también el KpiBar del layout y el snapshot de Muelle —
  // eso evita el "tengo que refrescar la pantalla" reportado por el
  // usuario, donde reservaciones se actualizaba pero KPIs y muelle
  // quedaban con datos viejos hasta un reload duro.
  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ['booking'] });
    queryClient.invalidateQueries({ queryKey: ['muelle'] });
    return q.refetch();
  };
  return { ...q, reservaciones: q.data || [], refetch };
}

export function useKpis() {
  const empresaId = getEmpresaId();
  return useQuery({
    queryKey: ['booking', 'kpis', empresaId],
    queryFn: () => fetchKpis(empresaId),
    staleTime: 60 * 1000,
  });
}

/** Particiona las reservaciones en las 5 categorías que muestran las sub-páginas. */
export function categorizar(reservaciones = []) {
  const hoy = todayLocal();
  const out = { hoy: [], proximas: [], porCobrar: [], historico: [], hold: [] };
  reservaciones.forEach(r => {
    const fecha = r.salida_fecha?.slice(0, 10);
    const estado = r.estado || '';
    const saldo = Number(r.saldo_pendiente || 0);
    const esCerrada = estado === 'completada' || estado === 'cancelada' || estado === 'no_show';

    // Hold sigue siendo su propia pestaña — son reservas pendientes de pago Bold/Epayco.
    if (estado === 'hold') {
      out.hold.push(r);
      return;
    }
    // La FECHA decide en qué pestaña vive la reserva, no el estado. El estado
    // se renderiza como chip dentro de la fila (EstadoChip). Esto evita que
    // reservas del día de hoy en estado 'completada' (porque el scheduler las
    // cerró junto con la salida) se escondan en histórico aunque su fecha sea hoy.
    if (fecha === hoy) {
      out.hoy.push(r);
    } else if (fecha && fecha > hoy) {
      out.proximas.push(r);
    } else if (fecha && fecha < hoy) {
      out.historico.push(r);
    }
    // Por cobrar: solo reservas vivas con saldo > 0. Una cancelada/completada
    // no se cobra.
    if (saldo > 0 && !esCerrada) {
      out.porCobrar.push(r);
    }
  });
  return out;
}
